import type { Contact } from '@/types/report-types';
import type { UserProfile } from '@/actions/ContactsLookup';

/**
 * Canonical mapper from a raw users-service / MixRank `UserProfile` into the
 * dashboard `Contact` shape.
 *
 * This is the SINGLE source of truth for profile → contact translation. Both
 * the client-side lazy company-contact loaders (BranchOutCompanies,
 * BranchOutCompaniesTabs, CompanyContactsFlat) and the server-side unified
 * search pipeline (via `mapContactRecordToEntity`) go through here, so every
 * surface that turns a profile into a contact uses exactly the same logic.
 *
 * The previous divergence — the pipeline casting a raw profile straight to
 * `ReportContact` — produced contacts with `id`/`fullName`/`email`/`company`
 * all `undefined` (the profile carries `profile_id`/`first_name`/
 * `preferred_emails`/`preferred_experience.company_name` instead), which the
 * Simple Search deck then silently dropped on its `!contact.id` filter.
 */
export function profileToContact(profile: UserProfile): Contact {
  const experiences = Array.isArray(profile.experience) ? profile.experience : [];
  const preferredExperience = profile.preferred_experience ?? undefined;
  const uniqueEmails = profile.preferred_emails ?? [];

  const title =
    typeof preferredExperience?.title === 'string' && preferredExperience.title.trim().length > 0
      ? preferredExperience.title
      : typeof profile.title === 'string'
        ? profile.title
        : '';

  const firstName = typeof profile.first_name === 'string' ? profile.first_name : '';
  const lastName = typeof profile.last_name === 'string' ? profile.last_name : '';
  const fullName = `${firstName} ${lastName}`.trim() || 'Unknown';

  const companyName =
    typeof preferredExperience?.company_name === 'string' &&
    preferredExperience.company_name.trim().length > 0
      ? preferredExperience.company_name
      : 'Unknown';

  return {
    id: profile.profile_id.toString(),
    firstName,
    lastName,
    fullName,
    title,
    company: companyName,
    companyId: preferredExperience?.linkedin_company_id?.toString() ?? 'unknown',
    email: uniqueEmails[0] ?? '',
    emails: uniqueEmails,
    phones: [],
    experience: experiences as unknown as Array<Record<string, unknown>>,
    education: [],
    emailSource: uniqueEmails[0]?.includes('@') ? 'verified' : 'predicted',
    linkedIn: typeof profile.url === 'string' ? profile.url : '',
    location: typeof profile.location === 'string' ? profile.location : '',
    seniority: 'mid',
    relevanceScore: 70,
    relevanceReason:
      typeof profile.headline === 'string' && profile.headline.trim().length > 0
        ? profile.headline
        : 'Decision-maker at target company',
    playbookId: '1',
    discoveredAt: new Date(),
    isFavorite: false,
    tags: [],
    hasOutreach: false,
    contactGroupKey: undefined,
    contactGroupLabel: undefined,
    status: 'not_contacted',
  };
}
